import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useState } from "react";

/**
 * Query defaults tuned for a console rather than for an app.
 *
 * `staleTime: 20s` — an operator moves between screens constantly, and
 * refetching a 1,700-row café list on every back-navigation is both slow
 * and pointless when the numbers move on the order of minutes.
 *
 * `refetchOnWindowFocus` stays ON. This is the one place it earns its
 * reputation: the console spends most of its life in a background tab while
 * something else is happening, and coming back to stale queue counts is
 * exactly the failure it prevents.
 *
 * `retry: 1` — a 401 or a 404 here means what it says, and retrying three
 * times only delays the error state the screen already knows how to draw.
 */
export function QueryProvider({ children }: { children: ReactNode }) {
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 20_000,
            gcTime: 5 * 60_000,
            retry: 1,
            refetchOnWindowFocus: true,
          },
          mutations: { retry: 0 },
        },
      }),
  );
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
